import compare from 'just-compare';
import {
  AiSettingsDraftUpdateMutationVariables,
  AiSettingsVersionFragment,
  SimilarityLevel,
} from 'lib-knowledge-management-common/graphql';
import { z } from 'zod';

/** The backend's bounds, checked here first so a save only fails for news. */
export const MAX_INSTRUCTIONS_LENGTH = 4000;
export const MIN_SIMILAR_CONVERSATIONS = 5;
export const MAX_SIMILAR_CONVERSATIONS = 10;

const instructionsSchema = z
  .string()
  // Counted the way the backend counts: after trimming.
  .refine((value) => value.trim().length <= MAX_INSTRUCTIONS_LENGTH, {
    message: `Keep instructions to ${MAX_INSTRUCTIONS_LENGTH.toLocaleString(
      'en',
    )} characters or fewer`,
  });

const similarConversationsMessage = `Enter a number from ${MIN_SIMILAR_CONVERSATIONS} to ${MAX_SIMILAR_CONVERSATIONS}`;

export const aiSettingsFormSchema = z.object({
  judgeInstructions: instructionsSchema,
  internalJudgeInstructions: instructionsSchema,
  writerInstructions: instructionsSchema,
  similarity: z.nativeEnum(SimilarityLevel),
  minSimilarConversations: z
    .number({ invalid_type_error: similarConversationsMessage })
    .int(similarConversationsMessage)
    .min(MIN_SIMILAR_CONVERSATIONS, similarConversationsMessage)
    .max(MAX_SIMILAR_CONVERSATIONS, similarConversationsMessage),
});

export type AiSettingsFormValues = z.infer<typeof aiSettingsFormSchema>;

/** Unset instructions come back as `null`; the form edits them as text. */
export function toFormValues(
  version: AiSettingsVersionFragment,
): AiSettingsFormValues {
  const { settings } = version;
  return {
    judgeInstructions: settings.judgeInstructions ?? '',
    internalJudgeInstructions: settings.internalJudgeInstructions ?? '',
    writerInstructions: settings.writerInstructions ?? '',
    similarity: settings.similarity,
    minSimilarConversations: settings.minSimilarConversations,
  };
}

/**
 * The draft is replaced whole, so every field goes. Blank instructions go as
 * `null`, which the backend reads as "none".
 */
export function toDraftVariables(
  values: AiSettingsFormValues,
): AiSettingsDraftUpdateMutationVariables {
  return {
    judgeInstructions: values.judgeInstructions.trim() || null,
    internalJudgeInstructions: values.internalJudgeInstructions.trim() || null,
    writerInstructions: values.writerInstructions.trim() || null,
    similarity: values.similarity,
    minSimilarConversations: values.minSimilarConversations,
  };
}

/** Whether two sets of values would save as the same settings. */
export function isSameSettings(
  a: AiSettingsFormValues,
  b: AiSettingsFormValues,
): boolean {
  return compare(toDraftVariables(a), toDraftVariables(b));
}
